import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors, closestCenter, pointerWithin } from "@dnd-kit/core";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import api, { apiError, moveIds } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, FolderPlus, Languages, Eye, Loader2 } from "lucide-react";
import { CategoryList } from "./menu/CategoryList";
import { ProductList } from "./menu/ProductList";
import { ProductDialog, LangTabs, fieldKey, aiTranslateFields } from "./menu/ProductDialog";
import { PublicMenu } from "@/pages/public/PublicMenu";

const empty = { name: "", name_bg: "", description: "", category_id: "", price: 0, weight: "", group: "", image: "", available: true, send_to_kitchen: true, show_on_website: true };

const collide = (args) => {
  const cat = pointerWithin(args).filter((c) => String(c.id).startsWith("cat:"));
  return cat.length ? cat : closestCenter({ ...args, droppableContainers: args.droppableContainers.filter((c) => !String(c.id).startsWith("cat:")) });
};

function CategoryDialog({ value, setValue, onSave }) {
  const [lang, setLang] = useState("bg");
  const [busy, setBusy] = useState(false);
  if (!value) return null;
  const set = (p) => setValue({ ...value, ...p });
  const translate = async () => {
    setBusy(true);
    try { set(await aiTranslateFields(value, ["name", "note"])); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && setValue(null)}>
      <DialogContent className="max-w-md" data-testid="category-dialog">
        <DialogHeader><DialogTitle>{value.id ? "Редактиране на категория" : "Нова категория"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <LangTabs lang={lang} setLang={setLang} testid="category" />
            <button type="button" onClick={translate} disabled={busy || !value.name_bg} className="flex items-center gap-1 text-xs text-gold-dark disabled:opacity-50" data-testid="category-ai-translate">
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Languages className="w-3 h-3" />} AI превод
            </button>
          </div>
          <div><Label className="text-xs">Име ({lang.toUpperCase()})</Label><Input value={value[fieldKey("name", lang)] || ""} onChange={(e) => set({ [fieldKey("name", lang)]: e.target.value })} data-testid="category-name" /></div>
          <div><Label className="text-xs">Бележка ({lang.toUpperCase()}, по избор)</Label><Textarea value={value[fieldKey("note", lang)] || ""} onChange={(e) => set({ [fieldKey("note", lang)]: e.target.value })} data-testid="category-note" /></div>
          <div><Label className="text-xs">ДДС % за тази категория</Label><Input type="number" min={0} max={100} value={value.vat_rate ?? 20} onChange={(e) => set({ vat_rate: Number(e.target.value) })} data-testid="category-vat" /></div>
        </div>
        <DialogFooter><Button onClick={onSave} className="bg-forest text-white rounded-xl" data-testid="save-category">Запази</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PreviewDialog({ open, onClose }) {
  const [lang, setLang] = useState("bg");
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto bg-cream" data-testid="menu-preview-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-3">Преглед на менюто <LangTabs lang={lang} setLang={setLang} testid="preview" /></DialogTitle></DialogHeader>
        {open && <PublicMenu key={lang} langOverride={lang} compact />}
      </DialogContent>
    </Dialog>
  );
}

export default function MenuManager() {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [prodDlg, setProdDlg] = useState(null);
  const [catDlg, setCatDlg] = useState(null);
  const [preview, setPreview] = useState(false);
  const [trProgress, setTrProgress] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  const load = async () => {
    const [c, p] = await Promise.all([api.get("/categories"), api.get("/products")]);
    setCategories(c.data); setProducts(p.data);
    setActiveCat((cur) => cur && c.data.some((x) => x.id === cur) ? cur : c.data[0]?.id || null);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const items = products.filter((p) => p.category_id === activeCat);
  const run = async (fn, ok) => { try { await fn(); if (ok) toast.success(ok); await load(); } catch (e) { toast.error(apiError(e)); } };

  const saveProduct = () => {
    const p = prodDlg;
    if (!p.name_bg?.trim() || !p.category_id) return toast.error("Име (БГ) и категория са задължителни");
    const moved = !p.id || products.find((x) => x.id === p.id)?.category_id !== p.category_id;
    const sort = moved ? products.filter((x) => x.category_id === p.category_id).length : p.sort;
    const payload = { ...p, name: p.name_bg.trim(), name_bg: p.name_bg.trim(), price: Number(p.price) || 0, sort };
    run(async () => { p.id ? await api.put(`/products/${p.id}`, payload) : await api.post("/products", payload); setProdDlg(null); }, "Записано и публикувано");
  };
  const toggle = (p) => run(() => api.put(`/products/${p.id}`, { ...p, available: !p.available }), p.available ? "Ястието е скрито" : "Ястието е видимо");
  const delProduct = (id) => window.confirm("Изтриване на ястието?") && run(() => api.delete(`/products/${id}`), "Изтрито");
  const duplicate = (p) => { const { id, ...rest } = p; setProdDlg({ ...rest, name_bg: `${p.name_bg || p.name} (копие)` }); };
  const moveCat = (i, dir) => { const ids = moveIds(categories, i, dir); if (ids) run(() => api.post("/categories/reorder", { ids })); };

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const p = products.find((x) => x.id === active.id);
    if (String(over.id).startsWith("cat:")) {
      const cid = over.id.slice(4);
      if (cid === p.category_id) return;
      const cname = categories.find((c) => c.id === cid)?.name_bg;
      return run(() => api.put(`/products/${p.id}`, { ...p, category_id: cid, sort: products.filter((x) => x.category_id === cid).length }), `Преместено в „${cname}“`);
    }
    const ids = items.map((x) => x.id);
    if (!ids.includes(over.id)) return;
    const next = arrayMove(ids, ids.indexOf(active.id), ids.indexOf(over.id));
    setProducts((ps) => ps.map((x) => (next.includes(x.id) ? { ...x, sort: next.indexOf(x.id) } : x)).sort((a, b) => a.sort - b.sort));
    run(() => api.post("/products/reorder", { ids: next }));
  };

  const saveCat = () => {
    const c = catDlg;
    if (!c.name_bg?.trim()) return toast.error("Име (БГ) е задължително");
    const payload = { ...c, name: c.name_bg.trim(), name_bg: c.name_bg.trim(), sort: c.id ? c.sort : categories.length };
    run(async () => { c.id ? await api.put(`/categories/${c.id}`, payload) : await api.post("/categories", payload); setCatDlg(null); }, "Записано");
  };
  const delCat = (id) => window.confirm("Изтриване на категорията и всички ястия в нея?") && run(() => api.delete(`/categories/${id}`), "Изтрито");

  const translateAll = async () => {
    if (!window.confirm("AI ще преведе всички липсващи преводи (EN, DE, FR). Съществуващите преводи няма да бъдат променени. Продължаване?")) return;
    let done = 0;
    for (const [i, c] of categories.entries()) {
      setTrProgress(`${i + 1}/${categories.length}`);
      try { done += (await api.post(`/ai/translate-category/${c.id}`)).data.translated; } catch (e) { toast.error(`${c.name_bg}: ${apiError(e)}`); }
    }
    setTrProgress(null);
    toast.success(`Преведени ястия: ${done}`);
    load();
  };
  const current = categories.find((c) => c.id === activeCat);

  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl text-forest-deep">Меню</h1>
          <div className="gold-rule w-24 my-3" />
          <p className="text-sm text-muted-foreground">Промените се публикуват веднага на сайта, в POS и в кухнята.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setPreview(true)} className="rounded-xl gap-2" data-testid="menu-preview-btn"><Eye className="w-4 h-4" /> Преглед</Button>
          <Button variant="outline" onClick={translateAll} disabled={!!trProgress} className="rounded-xl gap-2" data-testid="menu-translate-all">
            {trProgress ? <Loader2 className="w-4 h-4 animate-spin" /> : <Languages className="w-4 h-4" />} {trProgress ? `Превод ${trProgress}` : "AI превод на всичко"}
          </Button>
          <Button variant="outline" onClick={() => setCatDlg({ name_bg: "", note: "", vat_rate: 20 })} className="rounded-xl gap-2" data-testid="add-category-btn"><FolderPlus className="w-4 h-4" /> Категория</Button>
          <Button onClick={() => setProdDlg({ ...empty, category_id: activeCat || "", send_to_kitchen: current?.name_bg !== "Сосове и добавки" })} className="rounded-xl gap-2 bg-forest text-white" data-testid="add-product-btn"><Plus className="w-4 h-4" /> Ястие</Button>
        </div>
      </div>
      <DndContext sensors={sensors} collisionDetection={collide} onDragEnd={onDragEnd}>
        <div className="grid grid-cols-[minmax(0,1fr)] lg:grid-cols-[320px_minmax(0,1fr)] gap-5 mt-6">
          <CategoryList categories={categories} products={products} activeId={activeCat} onSelect={setActiveCat}
            onMove={moveCat} onEdit={setCatDlg} onDelete={delCat} />
          <div className="min-w-0">
            {current && <h2 className="font-display text-2xl text-forest-deep mb-1" data-testid="mm-current-category">{current.name_bg || current.name}</h2>}
            {current?.note && <p className="text-sm text-muted-foreground italic mb-3">{current.note}</p>}
            <ProductList items={items} onEdit={setProdDlg} onDelete={delProduct} onToggle={toggle} onDuplicate={duplicate} />
          </div>
        </div>
      </DndContext>

      <ProductDialog value={prodDlg} setValue={setProdDlg} categories={categories} onSave={saveProduct} />
      <CategoryDialog value={catDlg} setValue={setCatDlg} onSave={saveCat} />
      <PreviewDialog open={preview} onClose={() => setPreview(false)} />
    </div>
  );
}
